import Joi from 'joi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubFeatureEntry {
  key: string;
  displayName: string;
  type: 'boolean';
}

export interface FeatureEntry {
  key: string;
  displayName: string;
  permissionOptions?: readonly ('read' | 'edit')[];
  subFeatures?: SubFeatureEntry[];
  /**
   * When true the entry exists in the DB and validation layer but is not
   * rendered in the permission-management UI. Useful for deprecated or
   * system-managed permissions.
   */
  hidden?: boolean;
}

export interface ModuleEntry {
  key: string;
  displayName: string;
  /** 'internal' → CIS-only module, 'goGov' → GoGov-only module */
  visibility?: 'internal' | 'goGov';
  features: FeatureEntry[];
}

// ---------------------------------------------------------------------------
// Registry — single source of truth for every module / feature / sub-feature.
// To add a new permission, add it here and it propagates everywhere.
// ---------------------------------------------------------------------------

export const PERMISSIONS_REGISTRY: ModuleEntry[] = [
  {
    key: 'qcMailTicketing',
    displayName: 'QC, Mail, Ticketing',
    features: [
      {
        key: 'qcMailTicketingSchedules',
        displayName: 'Schedules',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'qcMailTicketingTables',
        displayName: 'Tables',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'qcMailTicketingResrules',
        displayName: 'ResRules',
        permissionOptions: ['read'],
      },
      {
        key: 'qcMailTicketingMessaging',
        displayName: 'Messaging',
        permissionOptions: ['edit'],
      },
      {
        key: 'qcMailTicketingOperations',
        displayName: 'Operations',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'qcMailTicketingReportingAndAnalytics',
        displayName: 'Reporting and Analytics',
        hidden: true,
      },
      {
        key: 'qcMailTicketingDataInsights',
        displayName: 'Data Insights',
        permissionOptions: ['read', 'edit'],
      },
    ],
  },
  {
    key: 'queueMonitor',
    displayName: 'Queue Monitor',
    features: [
      {
        key: 'queueMonitorConfiguration',
        displayName: 'Configuration',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'tableConnector',
    displayName: 'Table Connector',
    features: [
      {
        key: 'tableConnectorConfiguration',
        displayName: 'Configuration',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'tickettrak',
    displayName: 'TicketTRAK',
    features: [
      { key: 'tickettrakConfiguration', displayName: 'Configuration' },
    ],
  },
  {
    key: 'travelAuthorization',
    displayName: 'Travel Authorization',
    features: [
      {
        key: 'travelAuthorizationRequests',
        displayName: 'Requests',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'travelAuthorizationTravelPolicy',
        displayName: 'Travel Policy',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'travelAuthorizationAuthorizationFlow',
        displayName: 'Authorization Flow',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'travelAuthorizationMeetings',
        displayName: 'Meetings',
        permissionOptions: ['edit'],
      },
      {
        key: 'travelAuthorizationDynamicMeetings',
        displayName: 'Meetings & Events',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'travelAuthorizationBusinessInsights',
        displayName: 'Business Insights',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'travelAuthorizationTransactionLogs',
        displayName: 'Transaction Logs',
        permissionOptions: ['read'],
      },
      {
        key: 'travelAuthorizationDocumentLogs',
        displayName: 'Document Logs',
        permissionOptions: ['read'],
      },
    ],
  },
  {
    key: 'reports',
    displayName: 'Reports',
    features: [
      {
        key: 'reportsTravel',
        displayName: 'Travel',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'reportsOperations',
        displayName: 'Operations',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'reportsIbank',
        displayName: 'iBank',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'expediaReporting',
        displayName: 'Expedia',
        permissionOptions: ['read', 'edit'],
      },
    ],
  },
  {
    key: 'spendManagement',
    displayName: 'Spend Management',
    features: [
      {
        key: 'spendManagementReports',
        displayName: 'Reports',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'travelSpend',
    displayName: 'Travel + Spend',
    features: [
      {
        key: 'travelSpendReports',
        displayName: 'Reports',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'dataManagement',
    displayName: 'Data Management',
    features: [
      {
        key: 'dataManagementDataCleanser',
        displayName: 'Data Cleanser',
        permissionOptions: ['read'],
      },
      {
        key: 'dataManagementReports',
        displayName: 'Reports',
        permissionOptions: ['read', 'edit'],
      },
    ],
  },
  {
    key: 'eda',
    displayName: 'Event Driven Actions',
    features: [
      {
        key: 'edaConfiguration',
        displayName: 'Configuration',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'ibankReporting',
    displayName: 'iBank Reporting',
    features: [
      {
        key: 'ibankReportingLogin',
        displayName: 'Log in to iBank',
        permissionOptions: ['read'],
      },
      {
        key: 'ibankReportingEventLogs',
        displayName: 'Event Logs',
        permissionOptions: ['read'],
      },
      {
        key: 'ibankReportingHubLink',
        displayName: 'Hub Link in iBank',
        permissionOptions: ['read'],
      },
      {
        key: 'ibankReportingReports',
        displayName: 'Reports',
        permissionOptions: ['read', 'edit'],
      },
    ],
  },
  {
    key: 'administration',
    displayName: 'Administration',
    features: [
      {
        key: 'administrationClientManagement',
        displayName: 'Client Management',
        permissionOptions: ['read', 'edit'],
        subFeatures: [
          {
            key: 'administrationClientManagementClientIdentifiers',
            displayName: 'Client Identifiers',
            type: 'boolean',
          },
        ],
      },
      {
        key: 'administrationSiteConfiguration',
        displayName: 'Site Configuration',
        permissionOptions: ['edit'],
      },
      {
        key: 'administrationRoleManagement',
        displayName: 'Role Management',
        hidden: true,
      },
      {
        key: 'administrationAssetManagement',
        displayName: 'Asset Management',
        permissionOptions: ['edit'],
      },
      {
        key: 'administrationUserManagement',
        displayName: 'User Management',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'administrationDataAccessManagement',
        displayName: 'Data Access',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'administrationModuleAccessManagement',
        displayName: 'Module Access',
        permissionOptions: ['read', 'edit'],
      },
      {
        key: 'administrationUserImpersonation',
        displayName: 'User Impersonation',
        permissionOptions: ['edit'],
      },
      {
        key: 'administrationActivityLogs',
        displayName: 'Activity Logs',
        permissionOptions: ['read'],
      },
      {
        key: 'administrationBroadcasts',
        displayName: 'Broadcasts',
        permissionOptions: ['read', 'edit'],
      },
    ],
  },
  {
    key: 'cisInternal',
    displayName: 'CIS Internal Tools',
    visibility: 'internal',
    features: [
      {
        key: 'cisInternalOrganizations',
        displayName: 'Organizations',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalLibraries',
        displayName: 'Libraries',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalScheduleMover',
        displayName: 'Schedule Mover',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalEngineMonitor',
        displayName: 'Engine Monitor',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalQueueMonitor',
        displayName: 'Queue Monitor',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalIqcxAlertNotifications',
        displayName: 'iQCX Alert Notifications',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalEmailNotifications',
        displayName: 'Email Notifications',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalDynamicReporting',
        displayName: 'Dynamic Reporting',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalMeetingParameters',
        displayName: 'Parameter Configuration',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalMeetingTemplates',
        displayName: 'Template Builder',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalGtrStats',
        displayName: 'GTR Stats',
        permissionOptions: ['edit'],
      },
      {
        key: 'cisInternalFeatureFlags',
        displayName: 'Feature Flags',
        permissionOptions: ['edit'],
      },
    ],
  },
  {
    key: 'goGovQuicksightUserIngestion',
    displayName: 'QuickSight User Ingestion',
    visibility: 'goGov',
    features: [],
  },
  {
    key: 'quicksightReportingConfiguration',
    displayName: 'Reporting Configuration',
    visibility: 'goGov',
    features: [],
  },
  {
    key: 'goGovReportingGroups',
    displayName: 'GoGov Reporting Groups',
    visibility: 'goGov',
    features: [],
  },
  {
    key: 'goGovScheduling',
    displayName: 'GoGov Scheduling',
    visibility: 'goGov',
    features: [],
  },
];

// ---------------------------------------------------------------------------
// Accessor helpers — callers use these instead of filtering the array directly
// ---------------------------------------------------------------------------

/** Standard product modules (not internal, not goGov). */
export function getStandardModules(): ModuleEntry[] {
  return PERMISSIONS_REGISTRY.filter((m) => !m.visibility);
}

/** Features belonging to the cisInternal module. */
export function getInternalFeatures(): FeatureEntry[] {
  const mod = PERMISSIONS_REGISTRY.find((m) => m.key === 'cisInternal');
  return mod ? mod.features : [];
}

/** GoGov-specific modules. */
export function getGoGovModules(): ModuleEntry[] {
  return PERMISSIONS_REGISTRY.filter((m) => m.visibility === 'goGov');
}

// ---------------------------------------------------------------------------
// Derived artifacts — generated from the registry so every consumer stays in
// sync without maintaining its own copy.
// ---------------------------------------------------------------------------

type SqlColumnType = 'b' | 's';

/**
 * Replaces the hand-written `permissionsMapping` in sql/helpers.js.
 * Modules and boolean sub-features → 'b', features → 's'.
 */
export function buildPermissionsMapping(): Record<string, SqlColumnType> {
  const map: Record<string, SqlColumnType> = {};

  for (const mod of PERMISSIONS_REGISTRY) {
    map[mod.key] = 'b';
    for (const feat of mod.features) {
      map[feat.key] = 's';
      if (feat.subFeatures) {
        for (const sub of feat.subFeatures) {
          if (sub.type === 'boolean') map[sub.key] = 'b';
        }
      }
    }
  }

  return map;
}

// -- Joi helpers (same coercion logic previously in permissions.js) ----------

const moduleField = Joi.any().custom((value: unknown) =>
  [false, true].includes(value as boolean) ? value : false
);

const featureField = Joi.any().custom((value: unknown) =>
  ['edit', 'off', 'read'].includes(value as string) ? value : 'off'
);

const booleanField = Joi.any().custom((value: unknown) =>
  [false, true, 0, 1].includes(value as number) ? !!value : false
);

/**
 * Replaces the hand-written `hubPermissionsDefinition` Joi schema in
 * permissions.js. Module keys get moduleField, feature keys get featureField,
 * boolean sub-features get booleanField.
 */
export function buildJoiPermissionsSchema(): ReturnType<typeof Joi.object> {
  const shape: Record<string, ReturnType<typeof Joi.any>> = {};

  for (const mod of PERMISSIONS_REGISTRY) {
    shape[mod.key] = moduleField;
    for (const feat of mod.features) {
      shape[feat.key] = featureField;
      if (feat.subFeatures) {
        for (const sub of feat.subFeatures) {
          shape[sub.key] = sub.type === 'boolean' ? booleanField : featureField;
        }
      }
    }
  }

  return Joi.object(shape);
}

/**
 * Replaces the hand-written `hubPermissionsKeys` in permissions.js.
 *
 * Modules with features produce nested objects so callers can write
 * `hubPermissionsKeys.administration.administrationDataAccessManagement`.
 * Modules without features produce a plain string value.
 */
export function buildPermissionsKeys(): Record<
  string,
  string | Record<string, string>
> {
  const result: Record<string, string | Record<string, string>> = {};

  for (const mod of PERMISSIONS_REGISTRY) {
    if (mod.features.length === 0) {
      result[mod.key] = mod.key;
      continue;
    }

    const features: Record<string, string> = {};
    for (const feat of mod.features) {
      features[feat.key] = feat.key;
      if (feat.subFeatures) {
        for (const sub of feat.subFeatures) {
          features[sub.key] = sub.key;
        }
      }
    }
    result[mod.key] = features;
  }

  return result;
}
